/**
 * TaskCreateModal — 태스크 카드 생성
 *
 * 두 가지 모드:
 * 1. thread 모드: 쓰레드에서 AI 분석 → 제목/요약/담당자/마감일 제안
 * 2. manual 모드: 사용자가 직접 입력 (AI 분석 없음)
 *
 * 설계 원칙:
 * - 태그는 Records 전용 → 태스크 카드에는 없음
 * - 담당자는 채널 멤버에서 선택 가능
 * - manual 모드 생성 시 채팅에 시스템 메시지(푸시 없음) 삽입
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Trans, t } from '@lingui/macro';
import {
  ListTodo, Sparkles, Loader2, User, CalendarDays,
  CheckCircle2, ChevronDown, Plus, Paperclip, FileText, FolderOpen, X,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { UserAvatar } from '../components/UserAvatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { analyzeThread, TaskAnalysisResult } from '../../services/TaskAnalyzer';
import { createPost, fetchTasksBoard, checkExistingTasksByThread, notifyNewPost, uploadPostAttachment } from '../../services/BoardService';
import { My } from '../../services/My';
import { PostAuthor, TaskMetadata, PostAttachment, Board, canUseAllMention } from '../../models/Board';
import MentionTextArea from '../components/MentionTextArea';
import { UtilApp } from '../../services/Utils/UtilApp';
import { useMyRole } from '../../context/WorkspacePermissionContext';
import { useWorkspaceSettings } from '../../context/WorkspaceSettingsContext';
import { cn } from '../../lib/utils';
import { TaskEmitter } from './TaskEmitter';
import { ChatFilePickerModal } from '../board/ChatFilePickerModal';
import { Fire } from '../../services/FireClient';
import { Message } from '../../models/Message';

export type TaskCreateMode = 'thread' | 'manual';

interface TaskCreateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode?: TaskCreateMode;
  /** thread 모드에서 AI 분석 대상 텍스트 */
  threadText?: string;
  roomId: string;
  /** thread 모드에서 원본 메시지 ID */
  threadMessageId?: string;
  /** 태스크 출처 범위 — 'message'(해당 메시지만) | 'thread'(쓰레드 전체) */
  sourceScope?: 'message' | 'thread';
}

const TaskCreateModal: React.FC<TaskCreateModalProps> = ({
  open, onOpenChange, mode = 'thread', threadText, roomId, threadMessageId, sourceScope,
}) => {
  const { toast } = useToast();
  const [analyzing, setAnalyzing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [analysis, setAnalysis] = useState<TaskAnalysisResult | null>(null);

  // 편집 가능한 필드
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [assigneeUid, setAssigneeUid] = useState('');
  const [showAssigneePicker, setShowAssigneePicker] = useState(false);

  // 파일 첨부 — 게시판 게시글과 동일 패턴 (board-attachments 버킷)
  const [attachments, setAttachments] = useState<PostAttachment[]>([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const attachmentInputRef = React.useRef<HTMLInputElement>(null);
  // 채팅에서 파일 가져오기
  const [showChatFilePicker, setShowChatFilePicker] = useState(false);
  // 업로드 경로용 태스크 보드 (모달 열릴 때 미리 로드 → 첨부 시 boardId 확보)
  const [tasksBoard, setTasksBoard] = useState<Board | null>(null);

  const handleAttachmentSelect = useCallback(async (filesList: FileList | null) => {
    if (!filesList || filesList.length === 0 || !My.user?.id || !tasksBoard) return;
    setUploadingAttachment(true);
    try {
      const uploaded: PostAttachment[] = [];
      for (const f of Array.from(filesList)) {
        const r = await uploadPostAttachment(f, My.user.id, tasksBoard.id);
        uploaded.push({ ...r, source: 'local' });
      }
      setAttachments(prev => [...prev, ...uploaded]);
    } catch (err) {
      console.error('[TaskCreateModal] 첨부 업로드 실패:', err);
      toast({ title: t`Attachment upload failed`, variant: 'destructive' });
    } finally {
      setUploadingAttachment(false);
      if (attachmentInputRef.current) attachmentInputRef.current.value = '';
    }
  }, [tasksBoard, toast]);

  const removeAttachment = (idx: number) => setAttachments(prev => prev.filter((_, i) => i !== idx));

  // 모달 열릴 때 태스크 보드 로드 (첨부 업로드 경로에 필요)
  useEffect(() => {
    if (!open) { setTasksBoard(null); return; }
    let cancelled = false;
    fetchTasksBoard(roomId).then(b => { if (!cancelled) setTasksBoard(b); }).catch(() => {});
    return () => { cancelled = true; };
  }, [open, roomId]);

  // 채널 멤버 목록
  const channelMembers = useMemo(() => {
    const room = My.Rooms.get(roomId);
    if (!room) return [];
    return room.users.map(u => ({ id: u.id, name: u.name, photoUrl: u.photoUrl }));
  }, [roomId]);

  // 본문 @멘션 후보 (+ @전체 권한 시 최상단)
  const myRole = useMyRole();
  const wsSettings = useWorkspaceSettings();
  const canMentionAll = tasksBoard ? canUseAllMention(tasksBoard, myRole ?? 'guest', My.user?.id, wsSettings.board_manager_user_ids) : false;
  const mentionSuggestions = useMemo(() => {
    const base = channelMembers.map(m => ({ id: m.id, display: m.name }));
    return canMentionAll ? [{ id: 'everyone', display: t`Everyone` }, ...base] : base;
  }, [channelMembers, canMentionAll]);

  // AI 분석 실행 — thread 모드에서만 (+ 중복 태스크 검사)
  useEffect(() => {
    // 모달이 열릴 때 기본 담당자 = 본인 (thread 모드는 AI suggestedAssignee 가 덮어쓸 수 있음)
    if (open && My.user?.id) {
      setAssigneeUid(My.user.id);
    }
    if (open && mode === 'thread' && threadText?.trim()) {
      let cancelled = false;

      // 먼저 중복 검사
      if (threadMessageId) {
        checkExistingTasksByThread(threadMessageId, roomId).then(existing => {
          if (cancelled) return;
          // 같은 메시지라도 출처 범위(메시지/쓰레드)가 다르면 별개 태스크로 허용
          const existingPost = existing.find(p => p.metadata?.source_scope === sourceScope) ?? null;
          if (existingPost) {
            toast({
              title: t`A task has already been created from this thread.`,
              description: existingPost.title,
            });
            // 기존 태스크를 분할 뷰에서 표시
            TaskEmitter.emit('open', roomId, existingPost.id);
            onOpenChange(false);
            return;
          }
          // 중복 없으면 AI 분석 시작
          runAiAnalysis(cancelled);
        }).catch(() => {
          if (!cancelled) runAiAnalysis(cancelled);
        });
      } else {
        runAiAnalysis(cancelled);
      }

      function runAiAnalysis(isCancelled: boolean) {
        if (isCancelled) return;
        setAnalyzing(true);
        analyzeThread(threadText!, navigator.language || 'ko')
          .then(result => {
            if (cancelled) return;
            setAnalysis(result);
            setTitle(result.title);
            setSummary(result.summary);
            setDueDate(result.suggestedDueDate || '');
            if (result.suggestedAssignee) {
              const matched = channelMembers.find(
                m => m.name === result.suggestedAssignee || m.id === result.suggestedAssignee,
              );
              if (matched) setAssigneeUid(matched.id);
            }
          })
          .catch(err => {
            if (cancelled) return;
            console.error('[TaskCreateModal] AI 분석 실패:', err);
            toast({
              title: t`AI analysis failed. Please enter details manually.`,
              variant: 'destructive',
            });
            setAnalysis(null);
          })
          .finally(() => {
            if (!cancelled) setAnalyzing(false);
          });
      }

      return () => { cancelled = true; };
    }
    if (!open) {
      setAnalysis(null);
      setTitle('');
      setSummary('');
      setDueDate('');
      setAssigneeUid('');
      setShowAssigneePicker(false);
      setAttachments([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, threadText, mode]);

  // 카드 생성
  async function handleCreate() {
    if (!title.trim()) {
      toast({ title: t`Please enter a title.`, variant: 'destructive' });
      return;
    }

    setCreating(true);
    try {
      // 현재 채널의 태스크 보드 (모달 열릴 때 로드됨, 없으면 재조회)
      const board = tasksBoard ?? await fetchTasksBoard(roomId);
      if (!board) {
        toast({
          title: t`Tasks is not activated for this channel.`,
          variant: 'destructive',
        });
        return;
      }

      const now = new Date().toISOString();
      const author: PostAuthor = {
        uid: My.user.id,
        name: My.user.name,
        photoUrl: My.user.photoUrl || null,
      };

      // 담당자 객체 생성
      let assignee: PostAuthor | null = null;
      if (assigneeUid) {
        const member = channelMembers.find(m => m.id === assigneeUid);
        if (member) {
          assignee = {
            uid: member.id,
            name: member.name,
            photoUrl: member.photoUrl || null,
          };
        }
      }

      // 워쳐: 생성자 + 담당자 (중복 제거)
      const watchers = [My.user.id];
      if (assigneeUid && assigneeUid !== My.user.id) {
        watchers.push(assigneeUid);
      }

      const isManual = mode === 'manual';

      const metadata: TaskMetadata = {
        status: 'requested',
        assignee,
        creator: author,
        due_date: dueDate || null,
        source_type: isManual ? 'manual' : 'thread',
        source_thread_id: isManual ? null : (threadMessageId || null),
        source_scope: isManual ? null : (sourceScope ?? null),
        source_room_id: roomId,
        watchers,
        status_history: [{ from: null, to: 'requested', by: author, at: now }],
        last_activity_at: now,
      };

      const createdPost = await createPost({
        board_id: board.id,
        workspace_id: board.workspace_id,
        title: title.trim(),
        plain_text: summary,
        author,
        tags: [],  // 태스크 카드에는 태그 없음 (Records 전용)
        metadata: metadata as unknown as Record<string, any>,
        attachments: attachments.length > 0 ? attachments : undefined,
      });

      // 알림 발송 — 게시판 글과 동일. functions(board-notify)가 source='task'로 처리하고
      // 수신자별 prefs(notify_new_post)로 필터. (댓글 알림은 CommentSection이 이미 처리)
      if (createdPost) {
        const bodyMentionsRaw = UtilApp.extractMentionIds(summary);
        notifyNewPost(
          board.workspace_id,
          board.id,
          board.name,
          title.trim(),
          createdPost.id,
          bodyMentionsRaw.filter(id => id !== 'everyone'),
          bodyMentionsRaw.includes('everyone'),
        );
      }

      // manual 모드일 때 채팅에 시스템 메시지 삽입 (푸시 없음)
      if (isManual && createdPost) {
        try {
          const room = My.Rooms.get(roomId);
          if (room) {
            const sysMsg = new Message(room, My.user);
            sysMsg.autoMessage = 'task-created';
            sysMsg.text = title.trim();
            sysMsg.autoMessageItemId = createdPost.id;
            await Fire.msg.saveMessage(sysMsg);
          }
        } catch (err) {
          // 시스템 메시지 실패는 태스크 생성에 영향 없음
          console.warn('[TaskCreateModal] 시스템 메시지 전송 실패:', err);
        }
      }

      toast({ title: t`Task created` });
      TaskEmitter.emit('created', roomId, createdPost?.id);
      onOpenChange(false);
    } catch (err) {
      console.error('[TaskCreateModal] 생성 실패:', err);
      toast({
        title: t`Failed to create task`,
        variant: 'destructive',
      });
    } finally {
      setCreating(false);
    }
  }

  const selectedAssignee = channelMembers.find(m => m.id === assigneeUid);
  const isManual = mode === 'manual';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full max-w-full top-auto left-0 right-0 bottom-0 translate-x-0 translate-y-0 rounded-t-2xl rounded-b-none p-4 max-h-[92vh] overflow-y-auto data-[state=open]:slide-in-from-bottom-full data-[state=closed]:slide-out-to-bottom-full data-[state=open]:slide-in-from-top-0 data-[state=closed]:slide-out-to-top-0 sm:max-w-[640px] sm:top-1/2 sm:left-1/2 sm:right-auto sm:bottom-auto sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-2xl sm:p-6 sm:max-h-[92vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-heading-sm font-bold text-foreground">
            {isManual ? (
              <Plus className="size-[18px] text-amber-500" />
            ) : (
              <ListTodo className="size-[18px] text-amber-500" />
            )}
            {isManual ? (
              <Trans>New Task</Trans>
            ) : (
              <Trans>Create Task</Trans>
            )}
          </DialogTitle>
        </DialogHeader>

        {analyzing ? (
          <div className="flex flex-col items-center justify-center gap-3 py-12">
            <div className="relative">
              <Loader2 className="size-8 text-primary animate-spin" />
              <Sparkles className="size-4 text-amber-500 absolute -top-1 -right-1" />
            </div>
            <p className="text-sm text-muted-foreground">
              <Trans>AI is analyzing the conversation...</Trans>
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-4 pt-2">
            {/* 제목 */}
            <div>
              <label className="text-meta-sm font-semibold text-foreground mb-1 block">
                <Trans>Title</Trans>
              </label>
              <Input
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder={t`Enter a title for this task`}
                autoFocus
              />
            </div>

            {/* 요약 */}
            <div>
              <label className="text-meta-sm font-semibold text-foreground mb-1 block">
                <Trans>Content</Trans>
              </label>
              <MentionTextArea
                text={summary}
                onChange={setSummary}
                suggestions={mentionSuggestions}
                onSubmit={() => {}}
                placeholder={t`Describe the task in detail`}
                minHeight={210}
                className="rounded-md border border-border bg-transparent px-3 py-2 focus-within:ring-1 focus-within:ring-ring"
              />
            </div>

            {/* 담당자 */}
            <div>
              <label className="text-meta-sm font-semibold text-foreground mb-1 flex items-center gap-1">
                <User className="size-3" />
                <Trans>Assignee</Trans>
                <span className="text-meta-sm font-normal text-muted-foreground ml-1">(<Trans>optional</Trans>)</span>
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowAssigneePicker(!showAssigneePicker)}
                  className={cn(
                    'w-full flex items-center justify-between h-9 rounded-md border border-border px-3 text-sm cursor-pointer',
                    'hover:border-primary/40 transition-colors',
                    selectedAssignee ? 'text-foreground' : 'text-muted-foreground',
                  )}
                >
                  <span className="flex items-center gap-2">
                    {selectedAssignee ? (
                      <>
                        <UserAvatar
                          src={selectedAssignee.photoUrl ?? null}
                          name={selectedAssignee.name}
                          className="size-5"
                        />
                        {selectedAssignee.name}
                      </>
                    ) : (
                      <Trans>Select assignee</Trans>
                    )}
                  </span>
                  <ChevronDown className="size-3.5 text-muted-foreground" />
                </button>

                {showAssigneePicker && (
                  <div className="absolute top-full left-0 right-0 mt-1 z-50 rounded-md border border-border bg-popover shadow-md max-h-[180px] overflow-y-auto animate-in fade-in slide-in-from-top-1 duration-150">
                    {/* 미지정 옵션 */}
                    <button
                      type="button"
                      onClick={() => { setAssigneeUid(''); setShowAssigneePicker(false); }}
                      className={cn(
                        'w-full flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-muted transition-colors',
                        !assigneeUid && 'bg-primary/5 text-primary',
                      )}
                    >
                      <User className="size-4 text-muted-foreground" />
                      <span className="text-muted-foreground italic"><Trans>None</Trans></span>
                    </button>
                    {channelMembers.map(m => (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => { setAssigneeUid(m.id); setShowAssigneePicker(false); }}
                        className={cn(
                          'w-full flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-muted transition-colors',
                          assigneeUid === m.id && 'bg-primary/5 text-primary',
                        )}
                      >
                        <UserAvatar
                          src={m.photoUrl ?? null}
                          name={m.name}
                          className="size-5"
                        />
                        {m.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* 마감일 */}
            <div>
              <label className="text-meta-sm font-semibold text-foreground mb-1 flex items-center gap-1">
                <CalendarDays className="size-3" />
                <Trans>Due Date</Trans>
                <span className="text-meta-sm font-normal text-muted-foreground ml-1">(<Trans>optional</Trans>)</span>
              </label>
              <Input
                type="date"
                value={dueDate}
                onChange={e => setDueDate(e.target.value)}
              />
            </div>

            {/* 파일 첨부 — 게시판 게시글과 동일 패턴 */}
            <div className="flex flex-col gap-2">
              <label className="text-meta-sm font-semibold text-foreground flex items-center gap-1">
                <Paperclip className="size-3" />
                <Trans>Attachments</Trans>
                <span className="text-meta-sm font-normal text-muted-foreground ml-1">(<Trans>optional</Trans>)</span>
              </label>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => attachmentInputRef.current?.click()}
                  disabled={uploadingAttachment || !tasksBoard}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border text-meta-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer disabled:opacity-60"
                >
                  {uploadingAttachment ? <Loader2 className="size-3.5 animate-spin" /> : <Paperclip className="size-3.5" />}
                  <Trans>Attach file</Trans>
                </button>
                <button
                  type="button"
                  onClick={() => setShowChatFilePicker(true)}
                  disabled={uploadingAttachment}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border text-meta-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer disabled:opacity-60"
                >
                  <FolderOpen className="size-3.5" />
                  <Trans>Import from chat</Trans>
                </button>
                <input
                  ref={attachmentInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={e => handleAttachmentSelect(e.target.files)}
                />
                <span className="text-meta-sm text-muted-foreground"><Trans>Images · PDF · DOCX · ZIP etc.</Trans></span>
              </div>
              {attachments.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {attachments.map((att, idx) => (
                    <div
                      key={`${att.url}-${idx}`}
                      className="group inline-flex items-center gap-2 bg-muted/40 rounded-lg pl-2 pr-1 py-1.5 text-xs max-w-[280px]"
                    >
                      {att.group === 'image' ? (
                        <img src={att.url} alt={att.filename} className="size-8 rounded object-cover shrink-0" />
                      ) : (
                        <FileText className="size-4 text-muted-foreground shrink-0" />
                      )}
                      <span className="truncate flex-1 min-w-0 text-foreground/90">{att.filename}</span>
                      <button
                        type="button"
                        onClick={() => removeAttachment(idx)}
                        className="size-5 rounded hover:bg-destructive/15 text-muted-foreground hover:text-destructive transition-colors flex items-center justify-center"
                        title={t`Remove`}
                      >
                        <X className="size-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 액션 아이템 (읽기 전용 참고 — thread 모드에서만) */}
            {!isManual && analysis?.actionItems && analysis.actionItems.length > 0 && (
              <div>
                <label className="text-meta-sm font-semibold text-foreground mb-1 flex items-center gap-1">
                  <CheckCircle2 className="size-3" />
                  <Trans>Action Items</Trans>
                </label>
                <ul className="space-y-1">
                  {analysis.actionItems.map((item, i) => (
                    <li key={i} className="text-meta text-muted-foreground flex items-start gap-1.5">
                      <span className="mt-0.5 size-1.5 rounded-full bg-primary/40 shrink-0" />
                      <span className="min-w-0 break-words">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* 버튼 */}
            <div className="flex items-center justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={creating}
              >
                <Trans>Cancel</Trans>
              </Button>
              <Button
                onClick={handleCreate}
                disabled={creating || !title.trim()}
                className="gap-1.5"
              >
                {creating ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <ListTodo className="size-4" />
                )}
                <Trans>Create Task</Trans>
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
      <ChatFilePickerModal
        open={showChatFilePicker}
        channelId={roomId}
        onClose={() => setShowChatFilePicker(false)}
        onPick={(att) => {
          setAttachments(prev => [...prev, att]);
          setShowChatFilePicker(false);
        }}
      />
    </Dialog>
  );
};

export default TaskCreateModal;
